import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';

// ─── Intersection Observer Hook ───────────────────────────────────────────────
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
}

// ─── Data ─────────────────────────────────────────────────────────────────────
const plans = [
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 0,
    annualPrice: 0,
    description: 'Everything you need to get started with AI automation.',
    cta: 'Get Started Free',
    popular: false,
    features: [
      '3 workflows',
      '1,000 tasks / month',
      '1 AI agent',
      'Email support',
      'Basic analytics',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 99,
    annualPrice: 79,
    description: 'Scale your operations with advanced AI capabilities.',
    cta: 'Start Pro Trial',
    popular: true,
    features: [
      'Unlimited workflows',
      '50,000 tasks / month',
      '10 AI agents',
      'Priority 24/7 support',
      'Advanced analytics',
      'Team collaboration (5 seats)',
      'GPT-4o access',
      'Custom integrations',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthlyPrice: null,
    annualPrice: null,
    description: 'Tailored solutions for global organizations at scale.',
    cta: 'Talk to Sales',
    popular: false,
    features: [
      'Everything in Pro',
      'Unlimited AI agents',
      'Unlimited tasks',
      'Dedicated account manager',
      'SSO & SAML',
      'Custom AI training',
      'SLA guarantee',
      'Unlimited seats',
      'On-premise option',
    ],
  },
];

const comparisonRows = [
  { label: 'Workflows',         starter: '3',         pro: 'Unlimited',  enterprise: 'Unlimited' },
  { label: 'Tasks / month',     starter: '1,000',     pro: '50,000',     enterprise: 'Unlimited' },
  { label: 'AI Agents',         starter: '1',         pro: '10',         enterprise: 'Unlimited' },
  { label: 'Integrations',      starter: '10',        pro: '500+',       enterprise: '500+' },
  { label: 'Analytics',         starter: 'Basic',     pro: 'Advanced',   enterprise: 'Enterprise' },
  { label: 'Support',           starter: 'Email',     pro: '24/7 Priority', enterprise: 'Dedicated Manager' },
  { label: 'Team seats',        starter: '1',         pro: '5',          enterprise: 'Unlimited' },
  { label: 'SSO / SAML',        starter: false,       pro: false,        enterprise: true },
  { label: 'Custom AI training',starter: false,       pro: false,        enterprise: true },
  { label: 'SLA guarantee',     starter: false,       pro: true,         enterprise: true },
];

const benefits = [
  { icon: 'verified_user', title: 'Bank-Level Security',    desc: 'All data encrypted with AES-256 in transit and at rest. SOC 2 Type II certified for enterprise peace of mind.' },
  { icon: 'hub',           title: '500+ Integrations',      desc: 'Native connectors for every major platform. If it has an API, OrbitMind speaks its language out of the box.' },
  { icon: 'smart_toy',     title: '24/7 AI Monitoring',     desc: 'Intelligent agents watch your workflows around the clock, auto-healing failures before they impact your business.' },
  { icon: 'analytics',     title: 'ROI Analytics',          desc: 'Track hours saved and cost efficiency for every automation. Visualize your digital transformation impact in real time.' },
  { icon: 'sync',          title: 'Continuous Updates',     desc: 'Instant access to the latest AI models and automation features. New core updates ship every two weeks.' },
  { icon: 'lock',          title: 'AES-256 Encryption',     desc: 'Military-grade encryption protects every byte of your data, whether stored, in transit, or processed by AI agents.' },
];

const faqs = [
  {
    q: 'How quickly can I get started?',
    a: 'Instantly. Sign up and deploy your first workflow in under 10 minutes. No credit card required for the Starter plan.',
  },
  {
    q: 'Can I change plans anytime?',
    a: 'Yes, upgrade or downgrade at any time. Changes take effect immediately and billing is prorated automatically.',
  },
  {
    q: 'Do you offer a free trial?',
    a: 'Yes, the Starter plan is free forever. No credit card required. Pro and Enterprise plans include a 14-day free trial.',
  },
  {
    q: 'What happens if I exceed my task limit?',
    a: "We'll notify you at 80% usage. You can upgrade your plan or purchase additional task packs — your workflows never stop mid-execution.",
  },
];

// ─── Cell helper ──────────────────────────────────────────────────────────────
function Cell({ value }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/15">
        <span className="material-symbols-outlined text-primary text-base" style={{ fontSize: '16px' }}>check_circle</span>
      </span>
    );
  }
  if (value === false) {
    return <span className="text-gray-600 text-lg font-bold">—</span>;
  }
  return <span className="text-gray-300 text-sm font-medium">{value}</span>;
}

// ─── Main Component ────────────────────────────────────────────────────────────
const PricingPage = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('annual');
  const [openFaq, setOpenFaq] = useState(null);

  const [heroRef, heroInView]           = useInView(0.05);
  const [cardsRef, cardsInView]         = useInView(0.1);
  const [tableRef, tableInView]         = useInView(0.05);
  const [benefitsRef, benefitsInView]   = useInView(0.1);
  const [faqRef, faqInView]             = useInView(0.1);
  const [ctaRef, ctaInView]             = useInView(0.1);

  const getPrice = (plan) => {
    if (plan.monthlyPrice === null) return null;
    return billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice;
  };

  return (
    <div className="bg-dark text-white min-h-screen overflow-hidden font-display">

      {/* ── Announcement Bar ─────────────────────────────────────────────── */}
      <div className="bg-primary relative z-50">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-medium text-white">
          <span className="animate-pulse">✦</span>
          <span>OrbitMind 2.0 is here — the most powerful AI operating system yet.</span>
          <Link
            to="/announcement"
            className="underline underline-offset-2 hover:opacity-80 transition-opacity font-semibold whitespace-nowrap"
          >
            Learn more →
          </Link>
        </div>
      </div>

      {/* ── Navbar ───────────────────────────────────────────────────────── */}
      <Navbar />

      {/* ── Hero Section ─────────────────────────────────────────────────── */}
      <section ref={heroRef} className="relative overflow-hidden bg-dark pt-24 pb-20">

        {/* Mesh grid background */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `
                linear-gradient(rgba(19,164,236,0.15) 1px, transparent 1px),
                linear-gradient(90deg, rgba(19,164,236,0.15) 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px',
            }}
          />
          {/* Glowing orbs */}
          <div
            className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full opacity-20 blur-3xl"
            style={{ background: 'radial-gradient(circle, #13a4ec 0%, transparent 70%)' }}
          />
          <div
            className="absolute top-1/3 -right-60 w-[600px] h-[600px] rounded-full opacity-15 blur-3xl"
            style={{ background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)' }}
          />
        </div>

        <div className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>

          {/* Badge pill */}
          <div className="inline-flex items-center gap-2 bg-white/5 border border-primary/30 rounded-full px-4 py-2 mb-8 backdrop-blur-sm">
            <span className="material-symbols-outlined text-primary text-base" style={{ fontSize: '16px' }}>sell</span>
            <span className="text-sm font-semibold text-primary">Simple, transparent pricing</span>
          </div>

          {/* Headline */}
          <h1 className="text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight mb-6">
            <span className="text-white">Invest in automation,</span>
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(135deg, #13a4ec 0%, #7c3aed 50%, #ec4899 100%)' }}
            >
              not overhead.
            </span>
          </h1>

          {/* Subtext */}
          <p className="text-lg lg:text-xl text-gray-400 leading-relaxed mb-12 max-w-xl mx-auto">
            Start free. Scale as you grow. Cancel anytime.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-xl p-1 gap-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                billingCycle === 'monthly'
                  ? 'bg-white/10 text-white shadow-sm'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                billingCycle === 'annual'
                  ? 'bg-white/10 text-white shadow-sm'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Annual
              <span className="bg-emerald-500/20 text-emerald-400 text-xs font-bold px-2 py-0.5 rounded-full border border-emerald-500/30">
                Save 20%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* ── Pricing Cards ─────────────────────────────────────────────────── */}
      <section ref={cardsRef} className="pb-28 bg-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-6 items-start">

            {plans.map((plan, i) => {
              const price = getPrice(plan);
              const isPopular = plan.popular;

              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl p-px transition-all duration-700 ${
                    cardsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  } ${isPopular ? 'lg:scale-105 lg:-mt-4 z-10' : ''}`}
                  style={{
                    transitionDelay: `${i * 120}ms`,
                    background: isPopular
                      ? 'linear-gradient(135deg, #13a4ec, #7c3aed, #ec4899)'
                      : 'transparent',
                  }}
                >
                  {/* Card inner */}
                  <div
                    className={`rounded-2xl p-8 h-full flex flex-col ${
                      isPopular
                        ? 'bg-[#0d1a20]'
                        : 'bg-white/[0.03] border border-white/10 hover:border-white/20 transition-colors'
                    }`}
                  >
                    {/* Most Popular badge */}
                    {isPopular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                        <span
                          className="text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest"
                          style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}
                        >
                          Most Popular
                        </span>
                      </div>
                    )}

                    {/* Plan name */}
                    <div className="mb-6">
                      <h2 className="text-lg font-bold text-white mb-1">{plan.name}</h2>
                      <p className="text-gray-500 text-sm leading-relaxed">{plan.description}</p>
                    </div>

                    {/* Price */}
                    <div className="mb-8">
                      {price === null ? (
                        <div className="flex items-baseline gap-1">
                          <span className="text-5xl font-black text-white">Custom</span>
                        </div>
                      ) : (
                        <div className="flex items-baseline gap-1">
                          <span className="text-gray-400 text-xl font-semibold">$</span>
                          <span className="text-5xl font-black text-white">{price}</span>
                          <span className="text-gray-500 text-sm font-medium">/ month</span>
                        </div>
                      )}
                      {billingCycle === 'annual' && price !== null && price > 0 && (
                        <p className="text-emerald-400 text-xs font-semibold mt-1">
                          Billed annually · Save ${(plan.monthlyPrice - plan.annualPrice) * 12}/yr
                        </p>
                      )}
                      {price === 0 && (
                        <p className="text-gray-500 text-xs mt-1">Free forever · No credit card required</p>
                      )}
                    </div>

                    {/* CTA button */}
                    <button
                      onClick={() => {
                        if (plan.id === 'enterprise') navigate('/contact');
                        else navigate('/signup');
                      }}
                      className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-[1.02] mb-8 ${
                        isPopular
                          ? 'text-white hover:opacity-90'
                          : 'border border-white/20 bg-white/5 text-white hover:bg-white/10 hover:border-primary/40'
                      }`}
                      style={
                        isPopular
                          ? { background: 'linear-gradient(135deg, #13a4ec, #7c3aed)', boxShadow: '0 0 30px rgba(19,164,236,0.3)' }
                          : {}
                      }
                    >
                      {plan.cta}
                    </button>

                    {/* Divider */}
                    <div className="border-t border-white/10 mb-6" />

                    {/* Feature list */}
                    <ul className="space-y-3 flex-1">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-3">
                          <span
                            className="material-symbols-outlined text-primary shrink-0 mt-0.5"
                            style={{ fontSize: '18px' }}
                          >
                            check_circle
                          </span>
                          <span className="text-gray-300 text-sm leading-snug">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Feature Comparison Table ──────────────────────────────────────── */}
      <section ref={tableRef} className="py-28 bg-[#0b1620]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className={`text-center mb-16 transition-all duration-700 ${tableInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-4">
              Compare Plans
            </span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4">
              Everything side by side
            </h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              Pick the plan that fits your team. Upgrade or downgrade anytime.
            </p>
          </div>

          <div className={`rounded-2xl overflow-hidden border border-white/10 transition-all duration-1000 ${tableInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {/* Table header */}
            <div className="grid grid-cols-4 bg-white/[0.05] border-b border-white/10">
              <div className="p-5 text-sm font-bold text-gray-400 uppercase tracking-widest">Feature</div>
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`p-5 text-center ${plan.popular ? 'bg-primary/10' : ''}`}
                >
                  <span
                    className={`text-sm font-bold uppercase tracking-widest ${
                      plan.popular ? 'text-primary' : 'text-white'
                    }`}
                  >
                    {plan.name}
                  </span>
                </div>
              ))}
            </div>

            {/* Table rows */}
            {comparisonRows.map((row, i) => (
              <div
                key={row.label}
                className={`grid grid-cols-4 border-b border-white/5 last:border-0 transition-colors hover:bg-white/[0.02] ${
                  i % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.015]'
                }`}
              >
                <div className="p-5 text-sm text-gray-400 font-medium flex items-center">{row.label}</div>
                <div className="p-5 flex items-center justify-center">
                  <Cell value={row.starter} />
                </div>
                <div className="p-5 flex items-center justify-center bg-primary/[0.03]">
                  <Cell value={row.pro} />
                </div>
                <div className="p-5 flex items-center justify-center">
                  <Cell value={row.enterprise} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Benefits Grid ─────────────────────────────────────────────────── */}
      <section ref={benefitsRef} className="py-28 bg-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className={`text-center mb-16 transition-all duration-700 ${benefitsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-4">
              Why OrbitMind
            </span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4">
              Built for the enterprise
            </h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              Every layer engineered for reliability, security, and scale.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, i) => (
              <div
                key={benefit.title}
                className={`group bg-white/[0.03] border border-white/10 rounded-2xl p-8 hover:border-primary/40 hover:bg-white/[0.06] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(19,164,236,0.12)] ${
                  benefitsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-5 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                  <span className="material-symbols-outlined text-primary text-xl">{benefit.icon}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ Accordion ─────────────────────────────────────────────────── */}
      <section ref={faqRef} className="py-28 bg-[#0b1620]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className={`text-center mb-16 transition-all duration-700 ${faqInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-4">
              FAQ
            </span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4">
              Common questions
            </h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              Everything you need to know before getting started.
            </p>
          </div>

          <div className={`space-y-3 transition-all duration-700 delay-200 ${faqInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {faqs.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <div
                  key={i}
                  className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                    isOpen
                      ? 'border-primary/40 bg-white/[0.05]'
                      : 'border-white/10 bg-white/[0.03] hover:border-white/20'
                  }`}
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full flex items-center justify-between gap-4 p-6 text-left"
                    aria-expanded={isOpen}
                  >
                    <span className="text-white font-semibold text-base leading-snug">{faq.q}</span>
                    <span
                      className={`material-symbols-outlined text-primary shrink-0 transition-transform duration-300 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                      style={{ fontSize: '20px' }}
                    >
                      expand_more
                    </span>
                  </button>

                  {/* Answer panel */}
                  <div
                    className="overflow-hidden transition-all duration-300"
                    style={{ maxHeight: isOpen ? '200px' : '0px' }}
                  >
                    <p className="px-6 pb-6 text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ───────────────────────────────────────────────────── */}
      <section ref={ctaRef} className="py-28 relative overflow-hidden">
        {/* Gradient background */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, #0d1a20 0%, #0f1f2e 40%, #13a4ec18 100%)' }}
        />
        <div
          className="absolute inset-0 opacity-30"
          style={{ background: 'radial-gradient(ellipse at center, rgba(19,164,236,0.3) 0%, transparent 70%)' }}
        />

        <div
          className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${
            ctaInView ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-4 py-2 mb-8">
            <span className="material-symbols-outlined text-primary text-base" style={{ fontSize: '16px' }}>auto_awesome</span>
            <span className="text-sm font-semibold text-primary">Start free, scale infinitely</span>
          </div>

          <h2 className="text-5xl lg:text-7xl font-extrabold text-white mb-6 leading-tight">
            Start automating
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(135deg, #13a4ec, #7c3aed, #ec4899)' }}
            >
              today.
            </span>
          </h2>

          <p className="text-gray-400 text-xl mb-12 max-w-2xl mx-auto leading-relaxed">
            Join 2,000+ teams who've replaced manual operations with intelligent AI agents.
            Deploy your first workflow in under 10 minutes.
          </p>

          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => navigate('/signup')}
              className="group inline-flex items-center gap-2 px-10 py-5 rounded-xl font-bold text-white text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              style={{
                background: 'linear-gradient(135deg, #13a4ec, #7c3aed)',
                boxShadow: '0 0 40px rgba(19,164,236,0.4)',
              }}
            >
              <span>Get Started Free</span>
              <span className="material-symbols-outlined text-xl transition-transform group-hover:translate-x-1">arrow_forward</span>
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="inline-flex items-center gap-2 px-10 py-5 rounded-xl font-bold text-white text-lg border border-white/20 bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-primary/50 transition-all duration-300 hover:scale-105"
            >
              <span className="material-symbols-outlined text-xl text-primary">headset_mic</span>
              <span>Talk to Sales</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <Footer />

    </div>
  );
};

export default PricingPage;
